<template>
  <v-container>
    <v-row
      align="center"
      justify="end"
      class="mr-1"
    >
      <ActionButtons
        :actionButtons="getActionButtons"
        :dataCy="dataCy"
        :customDataCy="customDataCy"
      />
    </v-row>
  </v-container>
</template>

<script>
import Vue from 'vue';
import SendEmailToUserFormVue from '@/components/forms/SendEmailToUserForm.vue';
import { last, sortBy } from 'lodash';
import { mapGetters } from 'vuex';
import ActionButtons from '@/components/shared/ActionButtons.vue';

export default {
    components: { ActionButtons },
    props: {
        isCompleted: {
            type: Boolean,
            default: false,
        },
        isLocked: {
            type: Boolean,
            default: false,
        },
        reports: {
            type: Array,
            default: () => [],
        },
        workOrderId: {
            type: String,
            required: true,
        },
        onClickOnLocation: {
            type: Function,
            required: true,
        },
        canLeave: {
            type: Boolean,
        },
        mutableIsReopened: {
            type: Boolean,
            default: false,
        },
        onApprovePress: {
            type: Boolean,
            default: false,
        },
        dataCy: {
            type: String,
            default: 'buttonStatusWO',
        },
        customDataCy: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isEditable: false,
            isReopened: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
        ]),
        lastReport() {
            if (!Array.isArray(this.reports) || this.reports.length === 0) {
                return null;
            }

            // Sort by creation date
            const sorted = sortBy(this.reports, report => (new Date(report.createdAt)));

            return last(sorted);
        },
        getActionButtons() {
            return [
                {
                    id: 'btn-summary-arrive-leave',
                    label: this.canLeave ? this.$t('forms.btn.leaveSite') : this.$t('forms.btn.arrivedOnSite'),
                    action: this.onClickOnLocation,
                    icon: 'play_arrow',
                    color: 'primary',
                    displayIcon: true,
                    disabled: this.isCompleted || this.isLocked || !this.hasPermission('workOrders.edit'),
                },
                {
                    id: 'btn-visit-approve',
                    label: this.isReopened ? this.$t('visitSummary.reopen') : this.$t('visitSummary.approve'),
                    action: this.onClickApprove,
                    icon: 'done',
                    displayIcon: true,
                    color: 'primary',
                    disabled: ((this.isCompleted || !this.hasPermission('reports.add') || !this.hasPermission('workOrders.edit')) && !this.hasPermission('workOrders.reopen')) || this.isLocked || this.onApprovePress,
                },
                {
                    id: 'btn-preview-report',
                    label: this.$t(`visitSummary.${this.isCompleted || this.isLocked ? 'show' : 'preview'}Report`),
                    action: this.onClickReport,
                    icon: 'mdi-download',
                    displayIcon: true,
                    color: 'primary',
                    disabled: !this.hasPermission('reports.add') || !this.hasPermission('workOrders.view'),
                },
                {
                    id: 'btn-visit-send-email',
                    label: this.$t('visitSummary.sendByEmail'),
                    action: this.onClickSendEmail,
                    icon: 'mdi-email',
                    displayIcon: true,
                    color: 'primary',
                    disabled: !this.isCompleted,
                },
            ];
        },
    },
    watch: {
        mutableIsReopened(val) {
            this.isReopened = !!val;
        },
    },
    created() {
        if (this.isCompleted && this.hasPermission('workOrders.reopen')) {
            this.isReopened = true;
        }
    },
    methods: {
        async onClickReport() {
            this.$emit('loading', true);

            if (this.isCompleted || this.isLocked) {
                await this.showLastReport();
            } else {
                await this.showReportPreview();
            }

            this.$emit('loading', false);
        },
        async onClickApprove() {
            this.$emit('click-submit');
            this.$emit('isReopened', this.isReopened);
            this.$emit('isCompleted', this.isCompleted || this.isLocked);
        },
        async showReportPreview() {
            const fileName = `${this.$t('visitSummary.previewFileName', {
                date: (new Date()).getTime(),
            })}.pdf`;
            const { data } = await this.$rq.getWorkOrderReportPreview(this.workOrderId);
            this.$utils.saveFileOnDisk(data, fileName);
        },
        async showLastReport() {
            if (!this.lastReport) {
                this.$toasted.global.toast_error({
                    message: this.$t('visitSummary.reportNotFound'),
                });
                return;
            }

            const { file } = this.lastReport;
            const { data } = await this.$rq.downloadFile(file.id);
            this.$utils.saveFileOnDisk(data, file.fileName);
        },
        async onClickSendEmail() {
            this.$root.$emit('showModal', {
                title: this.$t('visitSummary.sendByEmail'),
                component: Vue.component('SendEmailToUserFormVue', SendEmailToUserFormVue),
                isForm: false,
                onConfirm: async requestPayload => {
                    await this.$rq.sendWorkOrderReportToUsers(this.workOrderId, requestPayload);
                    this.$toasted.global.toast_success({
                        message: this.$t('visitSummary.reportSent'),
                    });
                },
            });
        },
    },
};
</script>
